import { openNotification } from '@/common/helpers';
import { NotificationTypes } from '@/common/interfaces';
import { ChatMessage } from '@/components/ChatMessage';
import { Message } from '@/components/Message';
import {
  useGetMessagesMutation,
  useSendMessageMutation,
} from '@/store/api/chat.api';
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import styles from './SupportChat.module.scss';

interface MessageData {
  message: string;
  fileData: string;
  date: string;
  read?: boolean;
  formattedDate?: JSX.Element;
  client?: boolean;
  fileName?: string;
  edit?: boolean;
  name?: string;
}

const SupportChat: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const idTheme = id;
  const navigate = useNavigate();

  const chatWindowRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<MessageData[]>([]);
  const [page, setPage] = useState(1);
  const [showLoadMore, setShowLoadMore] = useState(false);

  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [sendMessage, { isLoading: loadingSendMessage }] =
    useSendMessageMutation();

  const [getMessages] = useGetMessagesMutation(); // Удалён неиспользуемый loadingGetMessages

  useEffect(() => {
    if (!idTheme) return;

    const fetchMessages = async () => {
      setLoadingMessages(true);
      try {
        const response = await getMessages({
          page: 1,
          id: idTheme,
        }).unwrap();

        setMessages(
          Array.isArray(response.Messages)
            ? [...response.Messages].reverse()
            : [],
        );
        setShowLoadMore(response.Messages?.length === 10);

        // Прокрутка в самый низ после загрузки сообщений
        if (chatWindowRef.current) {
          chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
        }
      } catch (error) {
        openNotification(
          NotificationTypes.ERROR,
          `Ошибка при загрузке сообщений: ${error}`,
        );
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [getMessages, idTheme]);

  const handleSendMessage = async (data: {
    message: string;
    fileData: string;
    fileName: string;
  }) => {
    if (!idTheme) return; // Ждём, пока idTheme не будет установлен

    try {
      const now = new Date().toISOString();

      await sendMessage({
        ...data,
        fileData: data.fileData,
        id: idTheme,
      }).unwrap();

      setMessages((prevMessages) => [
        ...prevMessages,
        { ...data, date: now, read: false, client: true },
      ]);

      openNotification(
        NotificationTypes.SUCCESS,
        'Сообщение успешно отправлено.',
      );
    } catch (error) {
      openNotification(
        NotificationTypes.ERROR,
        `Ошибка при отправке сообщения: ${error}`,
      );
    }
  };

  const loadMoreMessages = async () => {
    setLoadingMoreMessages(true);

    try {
      const response = await getMessages({
        page: page + 1,
        id: idTheme,
      }).unwrap();
      const newMessages = Array.isArray(response.Messages)
        ? [...response.Messages].reverse()
        : [];
      setMessages((prevMessages) => [...newMessages, ...prevMessages]);
      setPage((prevPage) => prevPage + 1);
      setShowLoadMore(newMessages.length === 10);
    } catch (error) {
      openNotification(
        NotificationTypes.ERROR,
        `Ошибка при загрузке сообщений: ${error}`,
      );
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  return !idTheme || loadingMessages ? (
    <div className={styles.loader}>
      <LoadingOutlined />
    </div>
  ) : (
    <div className={styles.container}>
      <div className={styles.header} onClick={() => navigate(-1)}>
        <ArrowLeftOutlined className={styles['header-backIcon']} />

        <span className={styles['header-name']}>
          Тема: {messages?.[0]?.name}
        </span>
      </div>

      <div className={styles.chatWindow} ref={chatWindowRef}>
        {showLoadMore && (
          <Button
            icon={<ArrowUpOutlined />}
            className={styles.loadMoreButton}
            onClick={loadMoreMessages}
            loading={loadingMoreMessages}
          >
            Предыдущие сообщения
          </Button>
        )}

        {!loadingMessages ? (
          messages.map((msg, index) => (
            <ChatMessage
              key={index}
              messageData={{
                ...msg,
                date: (
                  <div className={styles['date']}>
                    <span>{format(new Date(msg.date), 'dd.MM.yyyy')}</span>{' '}
                    {format(new Date(msg.date), 'HH:mm:ss')}
                  </div>
                ),
                read: msg.read ?? false,
              }}
            />
          ))
        ) : (
          <div className={styles.loader}>
            <LoadingOutlined />
          </div>
        )}
      </div>
      {messages[0]?.edit && (
        <Message isLoading={loadingSendMessage} onSend={handleSendMessage} />
      )}
    </div>
  );
};

export default SupportChat;
